import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { trackEvent } from "@/lib/analytics";
import { logger } from "@/lib/logger";
import type { PurchaseOffering, PurchaseResult } from "@/lib/native";
import { isNativeApp, nativeBridge } from "@/lib/native";
import { useAuthStore } from "@/store/authStore";

/** RevenueCat 구독 상품(offerings) 조회 */
export function useOfferingsQuery() {
  return useQuery({
    queryKey: ["offerings"],
    queryFn: () => nativeBridge.purchase.getOfferings(),
    enabled: isNativeApp,
    staleTime: 1000 * 60 * 10, // 10분
  });
}

/** isPlus 쿼리 캐시를 낙관적으로 true 설정 */
function setIsPlusCacheTrue(queryClient: ReturnType<typeof useQueryClient>, userId: string | undefined) {
  if (!userId) {
    return;
  }

  queryClient.setQueryData(["isPlus", userId], true);
}

/** 구독 구매 mutation */
export function usePurchaseMutation() {
  const queryClient = useQueryClient();
  const userId = useAuthStore(state => state.user?.id);

  return useMutation({
    mutationFn: async (packageId: string): Promise<PurchaseResult | null> => {
      logger.info({ domain: "purchase", message: "bridge 호출", data: { packageId } });
      const result = await nativeBridge.purchase.purchase(packageId);
      logger.info({ domain: "purchase", message: "bridge 응답", data: { result } });

      return result;
    },
    onSuccess: result => {
      logger.info({ domain: "purchase", message: "mutation 성공", data: { isPlus: result?.isPlus } });

      if (result?.isPlus) {
        setIsPlusCacheTrue(queryClient, userId);
      }
    },
    onError: error => {
      // 에러 원본은 native Sentry에서 캡처 — 웹은 breadcrumb만
      logger.warn({
        domain: "purchase",
        message: "mutation 실패",
        data: { errorMessage: error instanceof Error ? error.message : String(error) },
      });
      trackEvent("purchase_failed", {
        error_message: error instanceof Error ? error.message : String(error),
      });
    },
  });
}

/** 구매 복원 mutation */
export function useRestorePurchasesMutation() {
  const queryClient = useQueryClient();
  const userId = useAuthStore(state => state.user?.id);

  return useMutation({
    mutationFn: async (): Promise<PurchaseResult | null> => {
      logger.info({ domain: "purchase", message: "복원 bridge 호출" });
      const result = await nativeBridge.purchase.restorePurchases();
      logger.info({ domain: "purchase", message: "복원 bridge 응답", data: { result } });

      return result;
    },
    onSuccess: result => {
      logger.info({ domain: "purchase", message: "복원 mutation 성공", data: { isPlus: result?.isPlus } });

      if (result?.isPlus) {
        setIsPlusCacheTrue(queryClient, userId);
      }
    },
    onError: error => {
      // 에러 원본은 native Sentry에서 캡처 — 웹은 breadcrumb만
      logger.warn({
        domain: "purchase",
        message: "복원 mutation 실패",
        data: { errorMessage: error instanceof Error ? error.message : String(error) },
      });
      trackEvent("restore_purchases_failed", {
        error_message: error instanceof Error ? error.message : String(error),
      });
    },
  });
}

export type { PurchaseOffering, PurchaseResult };
